/* eslint-disable no-void */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';

import { queryClient } from 'utils/react-query';
import {
  getWorksheetFeedbackSummary,
  useGetFeedbackSummaryRunStatus,
  useStartFeedbackSummaryRun,
  worksheetFeedbackSummaryQueryKey,
} from './useWorksheets';

const DEFAULT_PROGRESS_MESSAGE = 'Fortschritt wird geladen...';
const INITIAL_PROGRESS = 5;
const INITIAL_PROGRESS_MESSAGE = '🧠 Die KI analysiert die abgegebenen Antworten...';

type UseFeedbackSummaryGenerationResult = {
  generateSummary: (worksheetId: string, classId?: string) => void;
  isLoading: boolean;
  progress: number;
  progressMessage: string;
  error: string | null;
  reset: () => void;
};

// New key: runs started before the workflow migration stored a job id under the old one
const getStorageKey = (worksheetId: string, classId?: string): string =>
  `feedback-summary-run:${worksheetId}:${classId ?? 'all'}`;

const removeStoredRun = (storageKey: string | null): void => {
  if (!storageKey) return;
  try {
    window.localStorage.removeItem(storageKey);
  } catch (storageError) {
    Sentry.captureException(storageError);
  }
};

export const useFeedbackSummaryGeneration = (
  worksheetId?: string,
  classId?: string
): UseFeedbackSummaryGenerationResult => {
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const isLoadingSummaryRef = useRef(false);

  const storageKey = useMemo(
    () => (worksheetId ? getStorageKey(worksheetId, classId) : null),
    [worksheetId, classId]
  );

  const startMutation = useStartFeedbackSummaryRun();
  const statusMutation = useGetFeedbackSummaryRunStatus();

  const stopRun = useCallback((): void => {
    setIsPolling(false);
    setCurrentRunId(null);
    removeStoredRun(storageKey);
  }, [storageKey]);

  const reset = useCallback(() => {
    setProgress(0);
    setProgressMessage('');
    setError(null);
    stopRun();
  }, [stopRun]);

  // The finished summary is the summary of this worksheet scope, so load it the same
  // way the page does and put it into the cache.
  const completeRun = useCallback(async (): Promise<void> => {
    if (!worksheetId || isLoadingSummaryRef.current) return;
    isLoadingSummaryRef.current = true;
    try {
      const summary = await getWorksheetFeedbackSummary(worksheetId, classId);
      if (!summary) {
        throw new Error('missing summary');
      }
      queryClient.setQueryData(worksheetFeedbackSummaryQueryKey(worksheetId, classId), summary);
      setProgress(100);
      setProgressMessage('');
    } catch (err) {
      await queryClient.invalidateQueries({
        queryKey: worksheetFeedbackSummaryQueryKey(worksheetId, classId),
      });
      setError('Die Zusammenfassung wurde erstellt, konnte aber nicht geladen werden.');
      Sentry.captureException(err);
    } finally {
      isLoadingSummaryRef.current = false;
      stopRun();
    }
  }, [worksheetId, classId, stopRun]);

  const pollRunStatus = useCallback(
    async (runId: string) => {
      if (statusMutation.isPending) return;

      try {
        const result = await statusMutation.mutateAsync(runId);
        setProgress(result.progress);
        setProgressMessage(result.progressMessage || DEFAULT_PROGRESS_MESSAGE);

        if (result.status === 'FAILED') {
          setError(result.failureReason || 'Die KI-Zusammenfassung konnte nicht erstellt werden.');
          stopRun();
          return;
        }

        if (result.status === 'COMPLETED') {
          await completeRun();
        }
        // PENDING / PROCESSING: keep polling
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Der Status der Zusammenfassung konnte nicht geladen werden.'
        );
        stopRun();
        Sentry.captureException(err);
      }
    },
    [statusMutation, completeRun, stopRun]
  );

  useEffect(() => {
    if (!isPolling || !currentRunId) return;

    const interval = setInterval(() => {
      void pollRunStatus(currentRunId);
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [isPolling, currentRunId, pollRunStatus]);

  // Resume a running generation for this scope (same browser) from localStorage
  useEffect(() => {
    setIsPolling(false);
    setCurrentRunId(null);
    setError(null);
    setProgress(0);
    setProgressMessage('');

    if (!storageKey) return;

    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;

      const parsed: { runId?: string } = JSON.parse(raw);
      if (!parsed?.runId) return;

      setCurrentRunId(parsed.runId);
      setIsPolling(true);
      setProgress(INITIAL_PROGRESS);
      setProgressMessage(INITIAL_PROGRESS_MESSAGE);
    } catch (storageError) {
      Sentry.captureException(storageError);
    }
  }, [storageKey]);

  const generateSummary = useCallback(
    async (targetWorksheetId: string, targetClassId?: string) => {
      reset();
      setProgress(INITIAL_PROGRESS);
      setProgressMessage(INITIAL_PROGRESS_MESSAGE);

      try {
        const { runId } = await startMutation.mutateAsync({
          worksheetId: targetWorksheetId,
          classId: targetClassId,
        });

        setCurrentRunId(runId);
        setIsPolling(true);

        try {
          window.localStorage.setItem(
            getStorageKey(targetWorksheetId, targetClassId),
            JSON.stringify({ runId, startedAt: Date.now() })
          );
        } catch (storageError) {
          Sentry.captureException(storageError);
        }

        void pollRunStatus(runId);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'Die KI-Zusammenfassung konnte nicht gestartet werden.'
        );
        Sentry.captureException(err);
      }
    },
    [reset, startMutation, pollRunStatus]
  );

  return {
    generateSummary,
    isLoading: startMutation.isPending || isPolling,
    progress,
    progressMessage,
    error,
    reset,
  };
};
